import { useState, useMemo } from 'react';
import Papa from 'papaparse';
import { Copy, Download, ArrowLeftRight } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { copyToClipboard, download } from '@/lib/utils';
import { toast } from 'sonner';

const SAMPLE_CSV = `name,role,city
Aarav,Engineer,Mumbai
Priya,Designer,Bengaluru
Rohan,PM,Pune`;

export function CsvJsonConvert() {
  const [direction, setDirection] = useState<'csv2json' | 'json2csv'>('csv2json');
  const [input, setInput] = useState(SAMPLE_CSV);

  const result = useMemo(() => {
    if (!input.trim()) return { ok: true, output: '', err: null };
    try {
      if (direction === 'csv2json') {
        const r = Papa.parse(input, { header: true, skipEmptyLines: true });
        if (r.errors.length) throw new Error(r.errors[0].message);
        return { ok: true, output: JSON.stringify(r.data, null, 2), err: null };
      }
      const data = JSON.parse(input);
      if (!Array.isArray(data)) throw new Error('Expected an array of objects');
      const csv = Papa.unparse(data as object[]);
      return { ok: true, output: csv, err: null };
    } catch (e) {
      return {
        ok: false,
        output: '',
        err: e instanceof Error ? e.message : 'Conversion error',
      };
    }
  }, [input, direction]);

  return (
    <div>
      <ToolHeader
        toolId={direction === 'csv2json' ? 'csv-to-json' : 'json-to-csv'}
        title="CSV ↔ JSON"
        description="Convert between CSV and JSON formats. Supports headers, mixed types, and bulk export."
      />

      <div className="flex items-center justify-between mb-4">
        <Tabs value={direction} onValueChange={(v) => {
          setDirection(v as typeof direction);
          setInput(v === 'csv2json' ? SAMPLE_CSV : '[\n  { "name": "Aarav", "role": "Engineer", "city": "Mumbai" }\n]');
        }}>
          <TabsList>
            <TabsTrigger value="csv2json">CSV → JSON</TabsTrigger>
            <TabsTrigger value="json2csv">JSON → CSV</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDirection((d) => (d === 'csv2json' ? 'json2csv' : 'csv2json'));
            setInput(result.output || input);
          }}
        >
          <ArrowLeftRight size={11} />
          Swap
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            {direction === 'csv2json' ? 'CSV input' : 'JSON input'}
          </div>
          <Textarea
            mono
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[400px] !bg-transparent focus-visible:!ring-0"
            invalid={!result.ok}
          />
        </Card>
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">
              {direction === 'csv2json' ? 'JSON output' : 'CSV output'}
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  const ok = await copyToClipboard(result.output);
                  if (ok) toast.success('Copied');
                }}
                disabled={!result.ok}
              >
                <Copy size={11} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const ext = direction === 'csv2json' ? 'json' : 'csv';
                  const mime = direction === 'csv2json' ? 'application/json' : 'text/csv';
                  download(result.output, `output.${ext}`, mime);
                }}
                disabled={!result.ok}
              >
                <Download size={11} />
              </Button>
            </div>
          </div>
          <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[400px] max-h-[480px]">
            {result.ok ? <code>{result.output}</code> : <code className="text-danger">{result.err}</code>}
          </pre>
        </Card>
      </div>
    </div>
  );
}

export default CsvJsonConvert;
